import React, { useState, useEffect } from 'react'
import { Megaphone, Send, FileText, BarChart3, Plus } from 'lucide-react'
import { AdminUser } from '../lib/authService'
import { supabase } from '../lib/supabase'
import {
  getAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  Announcement,
  SMSTemplate
} from '../services/communicationsService'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'

// Components
import CommunicationsDashboard from '../components/communications/CommunicationsDashboard'
import AnnouncementsList from '../components/communications/AnnouncementsList'
import AnnouncementForm from '../components/communications/AnnouncementForm'
import SMSCampaign from '../components/communications/SMSCampaign'
import SMSTemplates from '../components/communications/SMSTemplates'

type Tab = 'dashboard' | 'announcements' | 'sms' | 'templates'

interface CommunicationsProps {
  user?: AdminUser | null
}

const Communications: React.FC<CommunicationsProps> = ({ user }) => {
  const [activeTab, setActiveTab] = useState<Tab>('dashboard')
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [loading, setLoading] = useState(true)
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false)
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | undefined>()
  const [selectedTemplate, setSelectedTemplate] = useState<SMSTemplate | null>(null)
  const [churchId, setChurchId] = useState<string | null>(null)
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0)

  // Initialize church ID (with fallback to first church if not set)
  useEffect(() => {
    async function initializeChurchId() {
      if (user?.churchId) {
        setChurchId(user.churchId)
      } else {
        // Fallback: get first church (for demo/testing)
        const { data: churches } = await supabase.from('churches').select('id').limit(1)
        if (churches?.[0]?.id) {
          setChurchId(churches[0].id)
        }
      }
    }
    initializeChurchId()
  }, [user])

  useEffect(() => {
    if (churchId && activeTab === 'announcements') {
      loadAnnouncements()
    }
    // Refresh dashboard when switching to it
    if (activeTab === 'dashboard') {
      setDashboardRefreshKey(prev => prev + 1)
    }
  }, [churchId, activeTab])

  const loadAnnouncements = async () => {
    if (!churchId) return

    try {
      setLoading(true)
      const data = await getAnnouncements(churchId)
      setAnnouncements(data)
    } catch (error) {
      console.error('Failed to load announcements:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateAnnouncement = async (data: Partial<Announcement>) => {
    if (!churchId) return

    try {
      await createAnnouncement({ ...data, church_id: churchId })
      await loadAnnouncements()
      setShowAnnouncementForm(false)
    } catch (error) {
      console.error('Failed to create announcement:', error)
      throw error
    }
  }

  const handleUpdateAnnouncement = async (data: Partial<Announcement>) => {
    if (!editingAnnouncement) return

    try {
      await updateAnnouncement(editingAnnouncement.id, data)
      await loadAnnouncements()
      setShowAnnouncementForm(false)
      setEditingAnnouncement(undefined)
    } catch (error) {
      console.error('Failed to update announcement:', error)
      throw error
    }
  }

  const handleDeleteAnnouncement = async (id: string) => {
    try {
      await deleteAnnouncement(id)
      await loadAnnouncements()
    } catch (error) {
      console.error('Failed to delete announcement:', error)
    }
  }

  const handleEditAnnouncement = (announcement: Announcement) => {
    setEditingAnnouncement(announcement)
    setShowAnnouncementForm(true)
  }

  const handleSelectTemplate = (template: SMSTemplate) => {
    setSelectedTemplate(template)
    setActiveTab('sms')
  }

  if (!churchId) {
    return (
      <div className="flex flex-col gap-4 p-4 md:p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-destructive">Church ID not found. Please log in again.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Communications</h1>
          <p className="text-muted-foreground">Manage church communications and messaging</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as Tab)} className="space-y-4">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="dashboard">
              <BarChart3 className="w-4 h-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="announcements">
              <Megaphone className="w-4 h-4 mr-2" />
              Announcements
            </TabsTrigger>
            <TabsTrigger value="sms">
              <Send className="w-4 h-4 mr-2" />
              SMS Campaign
            </TabsTrigger>
            <TabsTrigger value="templates">
              <FileText className="w-4 h-4 mr-2" />
              Templates
            </TabsTrigger>
          </TabsList>

          {/* Quick Action Button */}
          {activeTab === 'announcements' && (
            <Button
              onClick={() => {
                setEditingAnnouncement(undefined)
                setShowAnnouncementForm(true)
              }}
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Announcement
            </Button>
          )}
        </div>

        {/* Content */}
        <TabsContent value="dashboard" className="space-y-4">
          <CommunicationsDashboard key={dashboardRefreshKey} churchId={churchId} />
        </TabsContent>

        <TabsContent value="announcements" className="space-y-4">
          <AnnouncementsList
            announcements={announcements}
            onEdit={handleEditAnnouncement}
            onDelete={handleDeleteAnnouncement}
            loading={loading}
          />

          {showAnnouncementForm && (
            <AnnouncementForm
              announcement={editingAnnouncement}
              churchId={churchId}
              onSubmit={editingAnnouncement ? handleUpdateAnnouncement : handleCreateAnnouncement}
              onCancel={() => {
                setShowAnnouncementForm(false)
                setEditingAnnouncement(undefined)
              }}
            />
          )}
        </TabsContent>

        <TabsContent value="sms" className="space-y-4">
          <SMSCampaign churchId={churchId} />
        </TabsContent>

        <TabsContent value="templates" className="space-y-4">
          <SMSTemplates onSelectTemplate={handleSelectTemplate} />
        </TabsContent>
      </Tabs>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Megaphone className="w-5 h-5 text-primary" />
              </div>
              <CardTitle className="text-base">Announcements</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <CardDescription>
              Create and manage church announcements. Target specific groups or roles, set dates, and pin important messages.
            </CardDescription>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Send className="w-5 h-5 text-primary" />
              </div>
              <CardTitle className="text-base">SMS Campaigns</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <CardDescription>
              Send bulk SMS to members. Select recipients by role or group, preview costs, and track delivery status.
            </CardDescription>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <CardTitle className="text-base">Templates</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <CardDescription>
              Pre-built message templates for common scenarios. Save time with welcome messages, event reminders, and more.
            </CardDescription>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default Communications
